import React, { useState, useRef, useEffect, type ChangeEvent } from 'react';
import {
  List,
  TextField,
  ListItemAvatar,
  Avatar,
  InputAdornment,
  IconButton,
  Typography,
  Box,
  useTheme,
  Card,
  CircularProgress,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import io, { type Socket } from 'socket.io-client';
import { useParams } from 'react-router-dom';
import LoadingButton from '@mui/lab/LoadingButton';
import { colorTheme } from 'theme';
import { type RootState } from 'store';
import { useSelector, useDispatch } from 'react-redux';
import {
  createChatMessages,
  getAllChatByConversationId,
  getConversationById,
  uploadChatImage,
} from 'service/ChatService';
import { type ChatMessagesAttributesType } from 'types/services';
import { YesNoModal, ImageViewModal } from 'components/shared';
import { createComplaints } from 'service/ComplaintService';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import { getCustomerById } from 'service';
import CloseIcon from '@mui/icons-material/Close';
import { GridSearchIcon } from '@mui/x-data-grid';
import { strings } from 'i18n/Strings';
import { customerChatsConstants } from 'constants/constantText';
import { setConversationId } from 'store/chat';
import { getAccessToken } from 'service/AuthService';
import { getResource } from 'service/UserService';
import ChatMessageItem from './ChatMessageItem';

interface SocketData {
  senderId: number;
  text: {
    text: string;
    image?: string;
  };
}

const CustomerChat = (): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const [message, setMessage] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessagesAttributesType[]>([]);
  const [chatDetails, setChatDetails] = useState<ChatMessagesAttributesType>(
    {},
  );
  const [name, setName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const [showAdditionalFields, setShowAdditionalFields] = useState(
    Array(messages.length).fill(false),
  );
  const [openComplaintDialog, setOpenComplaintDialog] =
    useState<boolean>(false);
  const [openImageDialog, setOpenImageDialog] = useState<boolean>(false);
  const [chatDescription, setChatDescription] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [viewImage, setViewImage] = useState(null);
  const [loader, setLoader] = useState(false);
  const [profileImage, setProfileImage] = useState('');

  const dispatch = useDispatch();
  const { id } = useParams();
  const { empId } = useSelector((state: RootState) => state.onboarding);

  const { conversationId } = useSelector((state: RootState) => state.chat);
  const receiverId = parseInt(id as string, 10);

  // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
  const UserId = receiverId + 'M';

  useEffect(() => {
    void handlegetAllMessages();
    void handleCustomerDetails();
  }, [id]);

  const handlegetAllMessages = async (): Promise<void> => {
    try {
      setLoader(true);
      const conversation = await getConversationById(Number(id));
      // Set userName even if there's no conversation yet
      if (conversation?.basicInfo?.userName) {
        setName(conversation?.basicInfo?.userName);
      }
      if (conversation?.basicInfo?.conversationId) {
        dispatch(setConversationId(conversation?.basicInfo?.conversationId));
        const res: { basicInfo: ChatMessagesAttributesType[] } =
          await getAllChatByConversationId(
            conversation?.basicInfo?.conversationId,
          );

        const profilePic = await getResource(
          conversation?.basicInfo?.user?.profile_image,
        );
        if (profilePic?.url) {
          setProfileImage(profilePic.url);
        }
        if (res?.basicInfo) {
          setMessages(res.basicInfo);
        }
      }
      setLoader(false);
    } catch (err) {
      console.log(err);
      setLoader(false);
    }
  };

  const handleCustomerDetails = async (): Promise<void> => {
    try {
      const res: {
        basicInfo: {
          email?: string;
          first_name?: string;
          last_name?: string;
          profile_image?: string;
        };
      } = await getCustomerById(receiverId);
      if (res?.basicInfo) {
        setEmail(res?.basicInfo?.email ?? '');
        // Set name from customer details if not already set
        const firstName = res?.basicInfo?.first_name;
        const lastName = res?.basicInfo?.last_name;
        if (!name && firstName && lastName) {
          setName(`${firstName} ${lastName}`);
        }
        // Get profile image if available
        if (res?.basicInfo?.profile_image) {
          const profilePic = await getResource(res?.basicInfo?.profile_image);
          if (profilePic?.url) {
            setProfileImage(profilePic.url);
          }
        }
      }
    } catch (err) {
      console.log(err);
    }
  };
  const accessToken = getAccessToken();
  useEffect(() => {
    socketRef.current = io(process.env.REACT_APP_MARSHMALLOW_API, {
      auth: {
        token: accessToken, // JWT
      },
    });
    socketRef.current.on('getMessage', (data: SocketData) => {
      setMessages((messages) => [
        ...messages,
        {
          meassageid: data?.senderId,
          message: data?.text?.text,
          senderType: 'customer',
          imageUri: data?.text?.image,
          fileType: data?.text?.type,
          createdAt: new Date().toISOString(),
        },
      ]);
    });

    socketRef.current.emit('addUser', UserId);

    // socketRef.current.on('getUsers', (users) => {
    // });

    // Clean up the socket when the component unmounts
    return () => {
      socketRef.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const sendMessage = (): void => {
    if (selectedImage) {
      handleSelectImage();
    } else {
      const newMessage = {
        message,
        senderType: 'employee',

        createdAt: new Date().toISOString(),
      };
      const createMessage = {
        conversationId,
        senderType: 'employee',
        senderId: empId,
        messagerType: 'conversation',
        message,
      };
      setMessages((messages) => [...messages, newMessage]);
      socketRef.current?.emit('sendMessage', {
        senderId: parseInt(UserId),
        receiverId,
        senderType: 'employee',
        text: newMessage,
        employeeId: empId,
      });
      void createChatMessages(createMessage);
      setMessage('');
    }
  };

  const handleComplaintDialogClose = (): void => {
    setOpenComplaintDialog(false);
  };

  const handleImageDialogClose = (): void => {
    setOpenImageDialog(false);
  };

  const handleComplaint = async (): Promise<void> => {
    setOpenComplaintDialog(false);
    const value = {
      firstName: name,
      senderType: 'employee',
      senderId: empId,
      email,
      complaintTitle: 'Chat mark as complaint',
      description: chatDetails?.message,
      imageUri: chatDetails?.imageUri,
      createdBy: empId,
    };

    if (value) {
      void (async () => {
        // create complaint service here
        createComplaints(value)
          .then((res) => {
            const { complaintId } = res?.result;

            dispatch(
              setSnackBarSuccess({
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                snackBarMessage: `${strings.displayText.complaintId}${complaintId}`,
              }),
            );
          })
          .catch((err) => {
            dispatch(
              setSnackBarFailed({ snackBarMessage: err.response.data.msg }),
            );
            console.error(`Something went wrong: ${String(err)}`);
          });
      })();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      sendMessage();
    }
  };

  const handleFileInputChange = (
    event: ChangeEvent<HTMLInputElement>,
  ): void => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const reader = new FileReader();
      setSelectedImage(file);
      reader.onload = (e) => {
        setMessage('');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSelectImage = (): void => {
    void uploadChatImage(selectedImage, Number(id)).then(
      (res: { chatImage: string }) => {
        if (res?.chatImage) {
          const createImgMessage = {
            conversationId,
            senderType: customerChatsConstants.employee,
            senderId: empId,
            message,
            messagerType: customerChatsConstants.conversation,
            imageUri: res?.chatImage,
            fileType: selectedImage?.type,
          };
          void createChatMessages(createImgMessage)
            .then((data) => {
              if (data) {
                const newMessage = {
                  id: messages.length + 1,
                  message,
                  senderType: customerChatsConstants.employee,
                  imageUri: res?.chatImage,
                  fileType: selectedImage?.type,
                  createdAt: new Date().toISOString(),
                };
                socketRef.current?.emit('sendMessage', {
                  senderId: parseInt(UserId),
                  receiverId,
                  text: newMessage,
                  senderType: customerChatsConstants.employee,
                  employeeId: empId,
                });
                setMessages((messages) => [...messages, newMessage]);
              }
            })
            .catch((err) => {
              console.log(err);
            });
        }
      },
    );
    setMessage('');
    setSelectedImage(null);
  };

  const filteredMessages = messages.filter((message) => {
    return message?.message.toLowerCase().includes(search.toLowerCase());
  });

  const customStyles = {
    mainCardStyle: {
      flexGrow: 1,
      overflowY: 'auto',
      display: 'flex',
      height: '85vh',
      flexDirection: 'column',
      borderRadius: '5px',
    },
    mainBoxStyle: {
      display: 'flex',
      padding: '10px 10px',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: '1px solid lightgray',
    },
    subBoxStyle: {
      display: 'flex',
      alignItems: 'center',
    },
    avatarBoxStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      borderRadius: '50%',
      border: '2px solid lightgray',
    },
    avatarNameStyle: {
      width: '24px',
      height: '24px',
      fontSize: '13px',
    },
    nameStyle: {
      fontSize: '13px',
      fontWeight: 600,
      marginLeft: '-15px',
    },
    inputFieldStyle: {
      marginRight: '20px',
      width: '416px',
      '& input': {
        borderColor: colors.solitude, // Change this to your desired color
      },
    },
    boxPaddingStyle: {
      padding: '5px',
    },
    msgContainerStyle: {
      flexGrow: 1,
      overflow: 'auto',
    },
    commonFlexStyle: {
      display: 'flex',
    },
    msgAvatarBoxStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      borderRadius: '50%',
      border: '2px solid lightgray',
    },
    msgAvatarStyle: {
      width: '24px',
      height: '24px',
      fontSize: '13px',
    },
    msgBoxStyle: {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: '-15px',
      marginRight: '-25px',
    },
    subMsgBoxStyle: {
      padding: '10px',
      maxWidth: '460px',
      minWidth: '100px',
      minHeight: '36px',
      borderRadius: '5px',
      wordWrap: 'break-word',
    },
    msgTypoStyle: {
      fontSize: '14px',
      fontWeight: '400',
      lineHeight: '24px',
    },
    cursorStyle: { cursor: 'pointer' },
    maxWidthStyle: {
      maxWidth: '100%',
    },
    pdfIconStyle: {
      width: 100,
      height: 100,
    },
    showBoxStyle: {
      display: 'flex',
      justifyContent: 'flex-end',
      cursor: 'pointer',
    },
    typoColorStyle: {
      color: colors.primaryRed[100],
    },
    chipBoxStyle: {
      display: 'flex',
      justifyContent: 'center',
      cursor: 'pointer',
      marginTop: '5px',
    },
    timeMainBoxStyle: {
      display: 'flex',
      flexDirection: 'column',
    },
    timestampStyle: {
      fontSize: '10px',
      fontWeight: 600,
      lineHeight: '20px',
    },
    textFieldBoxStyle: {
      display: 'flex',
      alignItems: 'center',
      padding: 1,
      justifyContent: 'center',
      textAlign: 'center',
    },
    imageStyle: {
      maxWidth: '50px',
      maxHeight: '50px',
    },
    closeIconStyle: {
      maxWidth: '15px',
      maxHeight: '15px',
      marginBottom: '20px',
      backgroundColor: colors.secondaryGreen[200],
      borderRadius: '10px',
    },
    fileIconStyle: {
      position: 'absolute',
      right: 0,
      top: '50%',
      marginRight: 10,
    },
    marginRightStyle: {
      marginRight: '10px',
    },
    sendButtonStyle: {
      bgcolor: colors.greenAccent[500],
      borderRadius: 2,
      py: 1.5,
    },
    displayNone: {
      display: 'none',
    },
    emptyPensionContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '70vh',
      width: '100%',
    },
    emptyPensionText: {
      fontFamily: 'Inter!important',
      color: '#838383',
      fontWeight: 500,
      fontSize: '1.125rem',
      lineHeight: 1.2,
    },
    profileImageStyle: {
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const,
    },
    logoImageStyle: {
      width: '100%',
      height: '100%',
      objectFit: 'contain' as const,
      padding: '2px',
    },
  };

  return (
    <Box>
      {loader ? (
        <Box sx={customStyles.emptyPensionContainer}>
          {' '}
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Card sx={customStyles.mainCardStyle}>
            <Box sx={customStyles.mainBoxStyle}>
              <Box sx={customStyles.subBoxStyle}>
                <ListItemAvatar>
                  <Box sx={customStyles.avatarBoxStyle}>
                    <Avatar sx={customStyles.avatarNameStyle}>
                      {profileImage ? (
                        <img
                          src={profileImage}
                          alt={name}
                          style={customStyles.profileImageStyle}
                        />
                      ) : (
                        name.slice(0, 1)
                      )}
                    </Avatar>
                  </Box>
                </ListItemAvatar>
                <Typography sx={customStyles.nameStyle}>{name}</Typography>
              </Box>
              <TextField
                label={strings.displayText.search}
                variant="outlined"
                size="small"
                fullWidth
                value={search}
                onChange={(event: {
                  target: { value: React.SetStateAction<string> };
                }) => {
                  setSearch(event.target.value);
                }}
                sx={customStyles.inputFieldStyle}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton>
                        <GridSearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          setSearch('');
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={customStyles.boxPaddingStyle}></Box>
            <Box sx={customStyles.msgContainerStyle}>
              <List>
                {filteredMessages.map((message, index) => (
                  <ChatMessageItem
                    key={message.messageId}
                    message={message}
                    index={index}
                    profileImage={profileImage}
                    name={name}
                    colors={colors}
                    customStyles={customStyles}
                    showAdditionalFields={showAdditionalFields}
                    setShowAdditionalFields={setShowAdditionalFields}
                    setOpenImageDialog={setOpenImageDialog}
                    setViewImage={setViewImage}
                    setOpenComplaintDialog={setOpenComplaintDialog}
                    setChatDescription={setChatDescription}
                    setChatDetails={setChatDetails}
                  />
                ))}
                <div ref={messagesEndRef} />
              </List>
            </Box>

            <Box sx={customStyles.textFieldBoxStyle}>
              <TextField
                label={strings.displayText.typeAMessage}
                variant="outlined"
                fullWidth
                value={message}
                onChange={(event: {
                  target: { value: React.SetStateAction<string> };
                }) => {
                  setMessage(event.target.value);
                }}
                onKeyDown={handleKeyDown}
                style={{ position: 'relative' }}
                InputProps={{
                  startAdornment: selectedImage && (
                    <InputAdornment position="start">
                      <img
                        src={URL.createObjectURL(selectedImage)}
                        alt="Selected"
                        style={customStyles.imageStyle}
                      />
                      <IconButton
                        onClick={() => {
                          setSelectedImage(null);
                        }}
                      >
                        <CloseIcon sx={customStyles.closeIconStyle} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      style={customStyles.fileIconStyle}
                    >
                      <IconButton
                        sx={customStyles.marginRightStyle}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <AttachFileIcon />
                      </IconButton>
                      <LoadingButton
                        type="submit"
                        variant="contained"
                        loading={false}
                        loadingPosition="end"
                        onClick={sendMessage}
                        sx={customStyles.sendButtonStyle}
                      >
                        <Typography component="h1" variant="h5">
                          {strings.displayText.send}
                        </Typography>
                      </LoadingButton>
                    </InputAdornment>
                  ),
                }}
              />

              <input
                type="file"
                ref={fileInputRef}
                style={customStyles.displayNone}
                onChange={handleFileInputChange}
                accept="image/*,application/pdf"
              />
            </Box>
          </Card>
        </>
      )}
      <YesNoModal
        open={openComplaintDialog}
        onClose={handleComplaintDialogClose}
        handleConfirm={handleComplaint}
        title={`${String(
          strings.displayText.createChatComplaint,
        )} ${chatDescription}`}
      />
      <ImageViewModal
        open={openImageDialog}
        onClose={handleImageDialogClose}
        viewImageUrl={viewImage}
        title={`${String(strings.displayText.imageView)}`}
      />
    </Box>
  );
};

export default CustomerChat;
